import { useState, useCallback, useContext, useRef } from 'react';
import { AuthContext } from '../../App.js';
import { buildChatRequestParams } from '../../api/chatPayload.js';

export const useChatHandlers = (
  getContext,
  nContext,
  historySearch,
  embeddings,
  getSelectedModel,
  getSelectedThinkingLevel,
  isFlow,
  vaultName
) => {
  const { vectorVault } = useContext(AuthContext);
  
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isChatLoading, setChatIsLoading] = useState(false);
  const [tempMessage, setTempMessage] = useState('');
  const [lastChunk, setLastChunk] = useState('');
  const [streamingCodeBlocks, setStreamingCodeBlocks] = useState({});
  const messageEndRef = useRef(null);

  const appendChatMessage = useCallback((message, isUserMessage) => {
    if (!message.trim()) return;

    setChatHistory((prev) => {
      const newHistory = [...prev, { message, isUserMessage }];
      localStorage.setItem('chatHistory', JSON.stringify(newHistory));
      return newHistory;
    });
  }, []);

  const handleError = useCallback(
    (error) => {
      appendChatMessage(error.message, false);
      localStorage.setItem(
        'chatHistory',
        JSON.stringify([...chatHistory, { message: error.message, isUserMessage: false }])
      );
      setChatIsLoading(false);
    },
    [chatHistory, appendChatMessage]
  );

  const handleStreamMessage = useCallback(
    (dataChunk) => {
      if (dataChunk === '!END') {
        setTempMessage('');
        setChatIsLoading(false);
        setLastChunk('');

        setChatHistory((prev) => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && /```[\s\S]*?```/.test(lastMsg.message)) {
            setStreamingCodeBlocks({});
          }
          return prev;
        });

        return;
      }

      let chunkToAppend = dataChunk;
      if (dataChunk.startsWith(lastChunk)) {
        chunkToAppend = dataChunk.slice(lastChunk.length);
      }
      setLastChunk(dataChunk);

      setChatHistory((prev) => {
        const lastIndex = prev.length - 1;
        const isLastBotMsg = prev.length > 0 && !prev[lastIndex].isUserMessage;

        const updatedMessage = isLastBotMsg
          ? prev[lastIndex].message + chunkToAppend
          : chunkToAppend;

        if (isLastBotMsg) {
          return [
            ...prev.slice(0, -1),
            { message: updatedMessage, isUserMessage: false },
          ];
        } else {
          return [...prev, { message: updatedMessage, isUserMessage: false }];
        }
      });
    },
    [lastChunk]
  );

  const handleFlowMessage = async (message, history) => {
    await vectorVault.runFlowStream(vaultName, message, history, null, null, {
      onMessage: handleStreamMessage,
      onLog: (logData) => console.log('Log:', logData),
      onError: handleError,
    });
  };

  const handleChatMessage = async (message, history, config) => {
    const params = buildChatRequestParams({
      vault: vaultName,
      text: message,
      history,
      get_context: getContext,
      n_context: nContext,
      return_context: config.returnContext,
      smart_history_search: historySearch,
      model: getSelectedModel(),
      embeddings_model: embeddings,
      ...config,
    }, getSelectedThinkingLevel());

    await vectorVault.getChatStream(params, handleStreamMessage);
  };

  const sendChatMessage = async () => {
    if (!chatMessage.trim()) return;

    const pastChatMessage = chatMessage;
    setChatMessage('');
    setChatIsLoading(true);
    setTempMessage('');

    appendChatMessage(pastChatMessage, true);

    const messageConfig = pastChatMessage.trim().endsWith('**')
      ? {
          metatag: ['item_id'],
          metatag_prefixes: ['\n**Item ID:'],
          metatag_suffixes: ['\n'],
          returnContext: true,
        }
      : {
          metatag: [],
          metatag_prefixes: [],
          metatag_suffixes: [],
          returnContext: false,
        };

    const fullHistory = chatHistory
      .map((item) =>
        item.isUserMessage ? `User: ${item.message}` : `Response: ${item.message}`
      )
      .join('\n');

    try {
      if (isFlow) {
        await handleFlowMessage(pastChatMessage, fullHistory);
      } else {
        await handleChatMessage(pastChatMessage, fullHistory, messageConfig);
      }
    } catch (error) {
      appendChatMessage(error.message, false);
      setChatIsLoading(false);
    }
  };

  const clearChat = useCallback(() => {
    setChatHistory([]);
    localStorage.removeItem('chatHistory');
  }, []);

  const handleTextareaKeydown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        // Submit on Enter, allow Shift+Enter for newline
        if (!e.shiftKey) {
          e.preventDefault();
          sendChatMessage();
        }
      }
    },
    [sendChatMessage]
  );

  return {
    chatMessage,
    setChatMessage,
    chatHistory,
    setChatHistory,
    isChatLoading,
    tempMessage,
    streamingCodeBlocks,
    messageEndRef,
    sendChatMessage,
    clearChat,
    handleTextareaKeydown,
    appendChatMessage,
  };
}; 